import * as React from 'react';
import { Table, CustomButton, ButtonType } from "soprasteria-ui-library";
import { IReferralTreatment } from '../../../../models/IReferral';
import { dateTimeToDateString } from '../../../../helpers/DateHelper';
import { ReferralAssessmentRejectModal } from '../ReferralModals/ReferralAssessmentRejctModal';
import './ReferralTreatmentReportingTable.scss';
import { ReferralTreatmentInfoModal } from '../ReferralModals/ReferralTreatmentInfoModal';
import { formatCurrency } from '../../../../helpers/CurrencyHelper';

interface IReferralTreatmentReportingTable {
    referralTreatments: IReferralTreatment[];
    onDelete: (treatmentId: number) => void;
    isEditable?: boolean;
    treatmentsTotalPrice?: number;
    canAssessTreatments?: boolean;
    acceptTreatment: (treatmentId: number) => void;
    rejectTreatment: (treatmentId: number, comment: string) => void;
    onTreatmentCredit: (patientTreatmentId: number) => void;
}

export const ReferralTreatmentReportingTable: React.FunctionComponent<IReferralTreatmentReportingTable> = ({ referralTreatments, onDelete, isEditable, treatmentsTotalPrice, canAssessTreatments, acceptTreatment, rejectTreatment, onTreatmentCredit }) => {
    const [referralTreatmentToAssess, setReferralTreatmentToAssess] = React.useState<number>();
    const [selectedTreatment, setSelectedTreatment] = React.useState<IReferralTreatment>();

    const removeTreatment = (patientTreatmentId: number) => {
        const removeTreatment = window.confirm("Är du säker på att du vill ta bort den här åtgärdskoden?");
        if (removeTreatment) {
            onDelete(patientTreatmentId);
        }
    }

    const onAcceptTreatmentButton = (id: number) => {
        const confirmedAccept = window.confirm("Är du säker på att du vill godkänna den här åtgärdskoden?")
        if (confirmedAccept) {
            acceptTreatment(id);
        }
    }

    const rejectTreatmentModalSubmit = (referralTreatmentId: number, comment: string) => {
        rejectTreatment(referralTreatmentId, comment);
        setReferralTreatmentToAssess(undefined);
    }

    const openTreatmentInfoModal = (treatment: IReferralTreatment) => {
        setSelectedTreatment(treatment);
        document.getElementById("open-referral-treatment-info-modal")?.click();
    }

    const renderClickableColumn = (text: string | undefined, treatment: IReferralTreatment, width?: number, textRight?: boolean) => {
        return (
            width ?
                <td style={{ width: `${width}%` }} onClick={() => openTreatmentInfoModal(treatment)} className={textRight ? "text-right text-nowrap" : ""}>{text}</td>
                :
                <td onClick={() => openTreatmentInfoModal(treatment)} className={textRight ? "text-right text-nowrap" : ""}>{text}</td>
        )
    }

    const creditTreatment = (patientTreatmentId: number) => {
        const creditTreatment = window.confirm("Är du säker på att du vill markera den här åtgärden för kreditering?");
        if (creditTreatment) {
            onTreatmentCredit(patientTreatmentId);
        }
    }

    const canAnyTreatmentBeRemoved = () => {
        return referralTreatments.some((treatment) => treatment.canBeRemoved);
    }

    const isAnyTreatmentCreditable = () => {
        return referralTreatments.some((treatment) => treatment.isCreditable);
    }

    return (
        <>
            <Table>
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Åtgärdskod</th>
                        <th>Antal</th>
                        <th className="text-right">à-pris</th>
                        <th className="text-right">Summa</th>
                        <th>Status</th>
                        <th>Behandlare</th>
                        <th>Motivering</th>
                        {
                            referralTreatments.length > 0 && (canAnyTreatmentBeRemoved() || isAnyTreatmentCreditable() || canAssessTreatments) ?
                                <th></th>
                                : null
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        referralTreatments.length > 0 ?
                            <>
                                {
                                    referralTreatments.map((treatment, key) =>
                                        <tr key={key} className="clickable-row">
                                            {renderClickableColumn(dateTimeToDateString(treatment.treatmentDate), treatment, 8)}
                                            {renderClickableColumn(`${treatment.treatmentCode} - ${treatment.treatmentCodeDescription}`, treatment)}
                                            {renderClickableColumn(treatment.quantity.toString(), treatment)}
                                            {renderClickableColumn(`${formatCurrency(treatment.price)}`, treatment, 6, true)}
                                            {renderClickableColumn(`${formatCurrency(treatment.quantity * treatment.price)}`, treatment, 6, true)}
                                            {renderClickableColumn(treatment.status, treatment)}
                                            {renderClickableColumn(treatment.carePerformerName, treatment, 12)}
                                            {renderClickableColumn(treatment.comment, treatment)}
                                            {
                                                canAnyTreatmentBeRemoved() || isAnyTreatmentCreditable() || canAssessTreatments ?
                                                    <td className="delete-btn">
                                                        {
                                                            treatment.isCreditable ?
                                                                <CustomButton
                                                                    buttonType={ButtonType.warning}
                                                                    onClick={() => creditTreatment(treatment.patientTreatmentId ?? 0)}
                                                                    buttonText="Kreditera" />
                                                                : null
                                                        }
                                                        {
                                                            treatment.canBeRemoved ?
                                                                <CustomButton
                                                                    buttonType={ButtonType.delete}
                                                                    onClick={() => removeTreatment(treatment.patientTreatmentId ?? 0)}
                                                                    buttonText="" />
                                                                : null
                                                        }
                                                        {
                                                            canAssessTreatments && treatment.status === 'Inväntar granskning' ?
                                                                <div className="assessment-buttons" role="group" aria-label="Assessment action buttons">
                                                                    <button aria-label="Approve" type="button" className="btn btn-success" onClick={() => onAcceptTreatmentButton(treatment.treatmentId)}>Godkänn</button>
                                                                    <button aria-label="Reject" type="button" className="btn btn-danger" onClick={() => setReferralTreatmentToAssess(treatment.treatmentId)} data-toggle="modal" data-target="#referral-assessment-reject-treatment-modal" >Neka</button>
                                                                </div>
                                                                : null
                                                        }
                                                    </td>
                                                    : null
                                            }
                                        </tr>
                                    )
                                }
                                <tr>
                                    <td colSpan={3}></td>
                                    <td colSpan={canAnyTreatmentBeRemoved() || isAnyTreatmentCreditable() || canAssessTreatments ? 6 : 4}>Totalt: {formatCurrency(treatmentsTotalPrice ?? 0)}</td>
                                </tr>
                            </>
                            :
                            <tr><td colSpan={isEditable ? 8 : 7}>Det finns inga registrerade åtgärdskoder</td></tr>
                    }
                </tbody>
            </Table>
            <CustomButton buttonType={ButtonType.create} buttonText="" data-toggle="modal" data-target="#referral-treatment-info-modal" id="open-referral-treatment-info-modal" hidden />
            {
                canAssessTreatments ?
                    <ReferralAssessmentRejectModal referralTreatmentId={referralTreatmentToAssess} onSubmit={rejectTreatmentModalSubmit} onFormClose={() => setReferralTreatmentToAssess(undefined)} />
                    : null
            }
            {
                referralTreatments.length > 0 ?
                    <ReferralTreatmentInfoModal treatment={selectedTreatment} onFormClose={() => setSelectedTreatment(undefined)} />
                    : null
            }
        </>
    )
}